import React, { useEffect, useState } from 'react';
import { Alert } from '@heroui/react';
import { MAIN_PANEL_OFFSET } from '../../../constants/constants';

export type AlertType = 'success' | 'error' | 'warning' | 'info';
export type AlertPosition = 'top-right' | 'top-left' | 'top-center' | 'bottom-right' | 'bottom-left' | 'bottom-center' | 'center';
export type AlertVariant = 'solid' | 'bordered' | 'flat' | 'faded';

export interface ISharedAlertProps {
	/** The type of alert which determines the color */
	type: AlertType;
	/** The message to display in the alert */
	message: string;
	/** Optional title for the alert */
	title?: string;
	/** Whether the alert is visible */
	isVisible: boolean;
	/** Callback when alert should be closed */
	onClose?: () => void;
	/** Auto-dismiss time in milliseconds (0 = no auto-dismiss) */
	autoDismissTime?: number;
	/** Position of the alert */
	position?: AlertPosition;
	/** Visual variant of the alert */
	variant?: AlertVariant;
	/** Custom className */
	className?: string;
	/** Custom panel offsets for specific positioning */
	panelOffsets?: { top?: number; left?: number; right?: number; bottom?: number };
	/** Whether to show close button */
	closable?: boolean;
	/** Custom z-index */
	zIndex?: number;
}

const SharedAlert: React.FC<ISharedAlertProps> = ({
	type,
	message,
	title,
	isVisible,
	onClose,
	autoDismissTime = 5000,
	position = 'top-center',
	variant = 'flat',
	className = '',
	panelOffsets = {},
	closable = true,
	zIndex = 50,
}) => {
	const [visible, setVisible] = useState(isVisible);

	useEffect(() => {
		setVisible(isVisible);
	}, [isVisible]);

	useEffect(() => {
		if (visible && autoDismissTime > 0) {
			const timer = setTimeout(() => {
				setVisible(false);
				onClose?.();
			}, autoDismissTime);

			return () => clearTimeout(timer);
		}
	}, [visible, autoDismissTime, onClose]);

	const handleClose = () => {
		setVisible(false);
		onClose?.();
	};

	if (!visible) {
		return null;
	}

	// Map alert types to HeroUI colors
	const getAlertColor = (alertType: AlertType) => {
		switch (alertType) {
			case 'success':
				return 'success';
			case 'error':
				return 'danger';
			case 'warning':
				return 'warning';
			case 'info':
				return 'primary';
			default:
				return 'default';
		}
	};

	// Calculate position styles
	const getPositionStyles = (): React.CSSProperties => {
		const baseOffsets = {
			top: panelOffsets.top ?? MAIN_PANEL_OFFSET.top,
			left: panelOffsets.left ?? MAIN_PANEL_OFFSET.left,
			right: panelOffsets.right ?? MAIN_PANEL_OFFSET.right,
			bottom: panelOffsets.bottom ?? MAIN_PANEL_OFFSET.bottom,
		};

		const styles: React.CSSProperties = {
			position: 'fixed',
			zIndex,
		};

		switch (position) {
			case 'top-right':
				styles.top = baseOffsets.top;
				styles.right = baseOffsets.right;
				break;
			case 'top-left':
				styles.top = baseOffsets.top;
				styles.left = baseOffsets.left;
				break;
			case 'top-center':
				styles.top = baseOffsets.top;
				styles.left = '50%';
				styles.transform = 'translateX(-50%)';
				break;
			case 'bottom-right':
				styles.bottom = baseOffsets.bottom;
				styles.right = baseOffsets.right;
				break;
			case 'bottom-left':
				styles.bottom = baseOffsets.bottom;
				styles.left = baseOffsets.left;
				break;
			case 'bottom-center':
				styles.bottom = baseOffsets.bottom;
				styles.left = '50%';
				styles.transform = 'translateX(-50%)';
				break;
			case 'center':
				styles.top = '50%';
				styles.left = '50%';
				styles.transform = 'translate(-50%, -50%)';
				break;
			default:
				styles.top = baseOffsets.top;
				styles.right = baseOffsets.right;
		}

		return styles;
	};

	const alertContent = (
		<Alert
			color={getAlertColor(type)}
			variant={variant}
			title={title}
			description={message}
			isClosable={closable}
			onClose={closable ? handleClose : undefined}
			className={`min-w-[300px] max-w-[500px] ${className} rounded-none`}
			classNames={{
				base: 'shadow-lg',
				title: 'font-semibold',
				description: 'text-sm',
				closeButton: 'hover:bg-opacity-20 mt-2',
			}}            
		/>
	);

	return (
		<div style={getPositionStyles()}>
			{alertContent}
		</div>
	);
};

export default SharedAlert;
